import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const ATT_KEY = "sc_att_queue";
const GRADE_KEY = "sc_grade_queue";
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());

const read = (key) => {
  try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; }
};
const write = (key, q) => { localStorage.setItem(key, JSON.stringify(q)); emit(); };

export const readQueue = () => read(ATT_KEY);
export const readGradeQueue = () => read(GRADE_KEY);

export const enqueueAttendance = (item) => {
  const q = readQueue().filter((x) => !(x.student_id === item.student_id && x.date === item.date));
  q.push({ ...item, queued_at: new Date().toISOString() });
  write(ATT_KEY, q);
};

export const enqueueGrade = (item) => {
  const q = readGradeQueue().filter((x) => !(x.student_id === item.student_id && x.assignment_id === item.assignment_id));
  q.push({ ...item, queued_at: new Date().toISOString() });
  write(GRADE_KEY, q);
};

export const isNetworkError = (e) => !e?.response;

let syncing = false;

const flush = async (key, send) => {
  const q = read(key);
  if (q.length === 0) return { sent: 0, failed: 0 };
  let sent = 0;
  const remaining = [];
  for (const item of q) {
    try {
      await send(item);
      sent += 1;
    } catch (e) {
      if (isNetworkError(e)) remaining.push(item);
    }
  }
  write(key, remaining);
  return { sent, failed: remaining.length };
};

export const flushQueue = async () => {
  if (syncing || !navigator.onLine) return { sent: 0, failed: 0, grades: 0 };
  syncing = true;
  emit();
  try {
    const att = await flush(ATT_KEY, (i) =>
      i.status === "_clear"
        ? api.delete("/attendance/manual", { params: { student_id: i.student_id, date: i.date } })
        : api.post("/attendance/manual", { student_id: i.student_id, status: i.status, date: i.date, note: i.note || "" }));
    const gr = await flush(GRADE_KEY, (i) =>
      api.post("/submissions/direct", { assignment_id: i.assignment_id, student_id: i.student_id, final_score: i.final_score, teacher_feedback: i.teacher_feedback || "" }));
    return { sent: att.sent, failed: att.failed, grades: gr.sent, gradesFailed: gr.failed };
  } finally {
    syncing = false;
    emit();
  }
};

export const isSyncing = () => syncing;

export function useOfflineQueue() {
  const [, tick] = useState(0);
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const fn = () => tick((n) => n + 1);
    listeners.add(fn);
    const on = () => { setOnline(true); fn(); };
    const off = () => { setOnline(false); fn(); };
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { listeners.delete(fn); window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  return { queue: readQueue(), gradeQueue: readGradeQueue(), online, syncing };
}

export const cacheRoster = (kelas, date, students, statuses) => {
  localStorage.setItem(`sc_students_${kelas}`, JSON.stringify(students));
  localStorage.setItem(`sc_roster_${kelas}_${date}`, JSON.stringify({ statuses, at: Date.now() }));
};

export const readRoster = (kelas, date) => {
  try {
    const students = JSON.parse(localStorage.getItem(`sc_students_${kelas}`) || "null");
    const day = JSON.parse(localStorage.getItem(`sc_roster_${kelas}_${date}`) || "null");
    if (!students) return null;
    return { students, statuses: day?.statuses || {} };
  } catch { return null; }
};

export const cacheGradeRoster = (assignmentId, roster) =>
  localStorage.setItem(`sc_grade_roster_${assignmentId}`, JSON.stringify(roster));

export const readGradeRoster = (assignmentId) => {
  try { return JSON.parse(localStorage.getItem(`sc_grade_roster_${assignmentId}`) || "null"); } catch { return null; }
};
